<template>
  <aside class="home-sidebar">
    <!-- 账号信息 -->
    <div class="sidebar-block account-block">
      <!-- 头像 + 所有个人信息（顶部对齐） -->
      <div class="profile-main-section">
        <div class="profile-avatar">
          <Avatar
            size="large"
            :avatar-index="userStore.avatarIndex"
            :userId="userStore.uid"
            :avatarFrame="userStore.avatarFrame"
          />
        </div>
        <div class="profile-info">
          <div class="profile-name-row">
            <div class="profile-name">
              {{ userStore.username || t("home.pleaseLogin") }}
            </div>
            <div
              v-if="userStore.userTitle && userStore.userTitle.length > 0"
              class="profile-titles"
            >
              <span
                v-for="(title, index) in userStore.userTitle"
                :key="index"
                class="profile-title-badge"
              >
                {{ title }}
              </span>
            </div>
          </div>
          <div v-if="roleDisplayText" class="profile-role-text">
            {{ roleDisplayText }}
          </div>

          <div class="profile-id-row">
            <div class="profile-id">
              {{ t("profile.communityId") }}：{{ userStore.uid || "-" }}
            </div>
          </div>
        </div>
      </div>
      <!-- 分割线 -->
      <div class="account-divider"></div>
      <!-- 切换角色按钮 -->
      <div class="role-actions">
        <button class="role-btn" @click="handleBindRole">
          {{ roleButtonText }}
        </button>
      </div>
    </div>

    <!-- 热门信息 -->
    <div
      class="sidebar-block hot-block"
      v-if="!hotLoading && hotList.length > 0"
    >
      <div class="hot-title">{{ t("search.hotTitle") }}</div>
      <div class="hot-list">
        <div
          v-for="(item, idx) in hotList"
          :key="item.id"
          class="hot-item"
          @click="handleHotItemClick(item)"
        >
          <span class="hot-index" :class="{ hot: item.hot }"
            >{{ idx + 1 }}.</span
          >
          <span class="hot-name">{{ item.name }}</span>
          <img v-if="item.hot" src="@/assets/hot.png" class="hot-icon" alt="" />
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useUserStore, useSearchStore } from "@/stores";
import { clickButton, pageType } from "@/utils/point";
// @ts-ignore
import Avatar from "@/components/common/Avatar.vue";
// @ts-ignore
import Loading from "@/components/common/Loading.vue";

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const searchStore = useSearchStore();

const hotLoading = ref(false);
const hotList = ref<{ id: number; name: string; hot: boolean }[]>([]);

const roleDisplayText = computed(() => {
  if (!userStore.isLogin) {
    return "";
  }
  if (userStore.bindInfo?.role_id) {
    const bindInfo = userStore.bindInfo;
    try {
      const serverName = bindInfo.server_name
        ? decodeURIComponent(bindInfo.server_name)
        : "";
      const roleName = bindInfo.role_name
        ? decodeURIComponent(bindInfo.role_name)
        : "";
      return `${serverName}-${roleName}`;
    } catch {
      return `${bindInfo.server_name || ""}-${bindInfo.role_name || ""}`;
    }
  }
  return t("home.unboundRole");
});

const roleButtonText = computed(() => {
  if (!userStore.isLogin) {
    return t("home.loginNow");
  }
  if (userStore.bindInfo?.role_id) {
    return t("shop.switchRole");
  }
  return t("shop.bindRole");
});

function handleBindRole() {
  if (!userStore.checkLogin()) return;
  clickButton({
    eventType: pageType.USER,
    eventReason: "角色切换/绑定",
  });
  userStore.openBindModal();
}

const initHotKeywords = async () => {
  hotLoading.value = true;
  try {
    await searchStore.getHotKeywords();
    hotList.value = searchStore.hotKeywords.map(
      (keyword: any, index: number) => ({
        id: index + 1,
        name: typeof keyword === "object" ? keyword.keyword || keyword : keyword,
        hot: index < 3,
      })
    );
  } catch (e) {
    // 热词加载失败时静默处理，保持空列表
  } finally {
    hotLoading.value = false;
  }
};

const handleHotItemClick = (item: { name: string }) => {
  clickButton({
    eventType: pageType.SEARCH,
    eventReason: "点击搜索热词",
    eventValue: JSON.stringify({ keyword: item.name }),
  });
  searchStore.addSearchHistory(item.name);
  searchStore.setHasSearched(true);
  router.push({ path: "/search", query: { q: item.name } });
};

onMounted(() => {
  initHotKeywords();
});

onUnmounted(() => {
  hotList.value = [];
});
</script>

<style lang="scss" scoped>
$primary-color: #dd8632;
$text-color: #333;
$white: #fff;
$shadow-sm: 0 0 2px rgba(0, 0, 0, 0.1);
$border-radius: 20px;

.home-sidebar {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-self: flex-start;
}

.sidebar-block {
  background: $white;
  border-radius: $border-radius;
  box-shadow: $shadow-sm;
  padding: 16px;
}

.account-block {
  .profile-main-section {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .profile-avatar {
    flex-shrink: 0;
  }

  .profile-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .profile-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 4px;
  }

  .profile-name {
    font-size: 16px;
    font-weight: 600;
    color: $text-color;
  }

  .profile-titles {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .profile-title-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    font-size: 10px;
    font-weight: 500;
    color: #fff;
    background: linear-gradient(135deg, #ffb400 0%, #ff9500 100%);
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(255, 180, 0, 0.25);
    white-space: nowrap;
    line-height: 1.2;
  }

  .profile-id-row {
    display: flex;
    align-items: center;
    margin: 4px 0;
  }

  .profile-id {
    color: #999;
    font-size: 12px;
  }

  .profile-role-text {
    color: #666;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .account-divider {
    border-top: 1px solid #f0f0f0;
    margin-top: 12px;
    padding-top: 12px;
  }

  .role-actions {
    display: flex;
    justify-content: flex-end;
  }

  .role-btn {
    padding: 6px 12px;
    border: 1px solid $primary-color;
    color: $primary-color;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    background: transparent;
    transition: all 0.2s;
    &:hover {
      background: rgba(221, 134, 50, 0.08);
    }
  }
}

.hot-block {
  .hot-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
    color: $text-color;
  }

  .hot-loading {
    display: flex;
    justify-content: center;
    padding: 16px 0;
  }

  .hot-empty {
    color: #999;
    font-size: 13px;
    padding: 8px 0;
  }

  .hot-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .hot-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #4a4a4a;
    cursor: pointer;
    padding: 4px 8px;
    margin: 0 -8px;
    border-radius: 6px;
    transition: color 0.2s, background-color 0.2s, transform 0.2s;
    &:hover {
      color: $text-color;
      background-color: rgba(0, 0, 0, 0.04);
      transform: translateX(4px);
    }
  }

  .hot-index {
    color: #ff9500;
    font-weight: 600;
    margin-right: 8px;
    flex-shrink: 0;
    &.hot {
      color: #ff3b30;
    }
  }

  .hot-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .hot-icon {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    margin-left: 4px;
  }
}
</style>
